import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router"
import { resendSignUpCode } from "aws-amplify/auth"
import { useEffect, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { requiredRule } from "@/utilities/form"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"

import styles from "./verify.module.css"

export const Route = createFileRoute("/auth/verify")({
  validateSearch: (
    search: Record<string, unknown>
  ): {
    email: string
    context: "sign-in" | "create-account"
  } => {
    return {
      email: (search.email as string) || "",
      context: (search.context as "sign-in" | "create-account") || "sign-in",
    }
  },
  beforeLoad: async ({ search }) => {
    // Redirect if neither email nor phone is provided
    const context = search.context
    if (!search.email) {
      throw redirect({
        to:
          context === "create-account"
            ? "/auth/create-account"
            : "/auth/sign-in",
        search,
      })
    }
    if (context === "sign-in") {
      await resendSignUpCode({ username: search.email })
    }
  },
  component: RouteComponent,
})

interface VerificationFormSchema {
  digit1: string
  digit2: string
  digit3: string
  digit4: string
  digit5: string
  digit6: string
}

// Helper function to log mutation errors to Sentry
function logMutationError(
  error: any,
  context: {
    flow: string
    email?: string
    qrLookupID?: string
    additionalData?: Record<string, any>
  }
) {
  Sentry.captureException(error, {
    extra: {
      email: context.email,
      qrLookupID: context.qrLookupID,
      originalError: error.message,
      ...context.additionalData,
    },
    tags: {
      section: "auth",
      flow: context.flow,
    },
  })
}

function useVerifyMutation(args: {
  form: ReturnType<typeof useForm<VerificationFormSchema>>
}) {
  const router = useRouter()
  const navigate = Route.useNavigate()
  const { t } = useTranslation(["errors", "gated"])
  const search = Route.useSearch()

  return useMutation({
    mutationFn: async (data: VerificationFormSchema) => {
      if (!search.email) {
        throw new Error(t("auth.emailRequiredVerification"))
      }

      const code = `${data.digit1}${data.digit2}${data.digit3}${data.digit4}${data.digit5}${data.digit6}`

      return api.auth.confirmSignUp({
        username: search.email,
        confirmationCode: code,
      })
    },
    onSuccess: async (result) => {
      if (
        search.context === "create-account" &&
        (result.nextStep?.signUpStep === "COMPLETE_AUTO_SIGN_IN" ||
          result.nextStep?.signUpStep === "DONE")
      ) {
        console.log("Attempting auto sign-in...")
        try {
          // TODO: move this into the mutation
          const autoSignInResult = await api.auth.autoSignIn()

          if (autoSignInResult.nextStep?.signInStep === "DONE") {
            // Auto sign-in successful after verification
            if (search.context === "create-account") {
              const redirect = search.redirect
                ? search.redirect
                : search.qrLookupID
                  ? router.buildLocation({
                      to: "/qr/$qrLookupID/welcome",
                      params: { qrLookupID: search.qrLookupID },
                    }).href
                  : router.buildLocation({ to: "/" }).href
              navigate({
                to: "/account/vehicles/create",
                search: {
                  qrLookupID: search.qrLookupID,
                  plate: search.plate,
                  redirect,
                },
              })
            } else if (search.redirect) {
              router.navigate({ to: search.redirect })
            } else if (search.qrLookupID) {
              console.log("Navigating to home")
              navigate({
                to: "/qr/$qrLookupID/welcome",
                params: { qrLookupID: search.qrLookupID },
              })
            } else {
              navigate({ to: "/" })
            }
            return
          } else {
            console.error(
              "Unhandled Auto sign-in step, nextStep:",
              autoSignInResult.nextStep
            )
            Sentry.captureException(
              new Error(
                `Unhandled Auto sign-in step, nextStep: ${autoSignInResult.nextStep}`
              )
            )
            throw new Error(t("common.somethingWentWrong", { ns: "errors" }))
          }
        } catch (autoSignInError) {
          // TODO we aren't showing the error to the user!
          console.error("Auto sign-in error:", autoSignInError)
          args.form.setError("root", {
            message:
              autoSignInError instanceof Error
                ? autoSignInError.message
                : t("common.somethingWentWrong", { ns: "errors" }),
          })
          logMutationError(autoSignInError, {
            flow: "auto-sign-in",
            email: search.email,
            qrLookupID: search.qrLookupID,
          })
        }
      } else if (result.nextStep?.signUpStep === "DONE") {
        navigate({
          to: "/auth/sign-in",
          search,
        })
        return
      } else {
        console.log(
          "Skipping auto sign-in - context:",
          search.context,
          "nextStep:",
          result.nextStep?.signUpStep
        )
        Sentry.captureException(
          new Error(
            `Unhandled sign-up step, nextStep: ${result.nextStep?.signUpStep}`
          )
        )
        args.form.setError("root", {
          message: t("common.somethingWentWrong", { ns: "errors" }),
        })
      }

      // TODO we need to go back and scrutinize this flow, we aren't properly handling the unhandled cases.
      // Default fallback - redirect to sign-in page
      console.log("Falling back to sign-in page")
      navigate({
        to: "/auth/sign-in",
        search,
      })
    },
    onError: (err: any) => {
      logMutationError(err, {
        flow: "verification",
        email: search.email,
        qrLookupID: search.qrLookupID,
        additionalData: { context: search.context },
      })

      if (
        err.message === t("auth.emailRequiredVerification", { ns: "errors" })
      ) {
        args.form.setError("root", { message: err.message })
      } else {
        handleAuthError(err, args.form, t)
      }
    },
  })
}

function useResendVerificationMutation({
  email,
  onSuccess,
  setError,
}: {
  email?: string
  onSuccess?: () => void
  setError: (args: { error: Error; onClose?: () => void }) => void
}) {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: async () => {
      if (!email) {
        throw new Error(t("auth.emailRequiredResend", { ns: "errors" }))
      }

      return api.auth.resendSignUpCode({
        username: email,
      })
    },
    onSuccess: () => {
      onSuccess?.()
    },
    onError: (err: any) => {
      logMutationError(err, {
        flow: "resend-verification",
        email,
      })
      setError({ error: err })
    },
  })
}

function RouteComponent() {
  const { email, context } = Route.useSearch()
  const { t } = useTranslation(["gated", "errors"])

  const form = useForm<VerificationFormSchema>({
    defaultValues: {
      digit1: "",
      digit2: "",
      digit3: "",
      digit4: "",
      digit5: "",
      digit6: "",
    },
    mode: "onChange",
  })

  const inputRefs = useRef<Array<HTMLInputElement | null>>([])

  const [showCodeSent, setShowCodeSent] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const { setError } = useErrorModal()

  const verifyMutation = useVerifyMutation({ form })

  const resendMutation = useResendVerificationMutation({
    email,
    onSuccess: () => {
      setShowCodeSent(true)
      setResendCountdown(30)
    },
    setError,
  })

  useEffect(() => {
    if (showCodeSent) {
      const timer = setTimeout(() => {
        setShowCodeSent(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [showCodeSent])

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCountdown])

  const onSubmit = (data: VerificationFormSchema) => {
    form.clearErrors("root")
    verifyMutation.mutate(data)
  }

  const handleResendCode = () => {
    if (resendCountdown > 0) return
    form.clearErrors("root")
    resendMutation.mutate()
  }

  const digitFields = [
    "digit1",
    "digit2",
    "digit3",
    "digit4",
    "digit5",
    "digit6",
  ] as const

  const handlePaste = (pastedText: string) => {
    const digits = pastedText.replace(/\D/g, "").slice(0, 6)
    if (digits.length === 0) return
    form.clearErrors("root")

    // Clear all fields first
    digitFields.forEach((fieldName) => {
      form.setValue(fieldName, "", { shouldValidate: false })
    })

    // Fill fields with available digits
    for (let i = 0; i < digits.length && i < digitFields.length; i++) {
      form.setValue(digitFields[i], digits[i], {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

    // Focus the next empty field, or the last field if all are filled
    const focusIndex = Math.min(digits.length, digitFields.length - 1)
    inputRefs.current[focusIndex]?.focus()
  }

  return (
    <>
      <Heading level={1} as={1}>
        {context === "create-account"
          ? t("auth.verify.headingCreateAccount", { ns: "common" })
          : t("auth.verify.headingSignIn", { ns: "common" })}
      </Heading>

      <Text level={2} className={styles.descriptionText}>
        {t("auth.verify.description", { ns: "common" })}{" "}
        {email && <strong className={styles.emailHighlight}>{email}</strong>}
        {!email && t("auth.verify.registeredContactMethod", { ns: "common" })}.
      </Text>

      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset disabled={verifyMutation.isPending}>
          {/* 6-Digit Code Input */}
          <div className={styles.codeInputRow}>
            {digitFields.map((fieldName, index) => (
              <Controller<VerificationFormSchema>
                key={fieldName}
                name={fieldName}
                control={form.control}
                rules={{
                  required: requiredRule(
                    t("common.required", { ns: "errors" })
                  ),
                  pattern: {
                    value: /^[0-9]$/,
                    message: t("common.singleDigit", { ns: "errors" }),
                  },
                }}
                render={({ field: { ref, ...rest } }) => {
                  return (
                    <TextField
                      aria-label={`Verification code digit ${index + 1}`}
                    >
                      <TextField.Input
                        {...rest}
                        innerRef={(el) => {
                          inputRefs.current[index] = el
                          if (typeof ref === "function") ref(el)
                        }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        className={styles.codeDigit}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "")

                          // Clear errors when user starts typing
                          if (form.formState.errors.root)
                            form.clearErrors("root")

                          if (val.length > 1) {
                            // Only handle paste if we're not at the last input or if current input is empty
                            if (index < digitFields.length - 1 || !rest.value) {
                              handlePaste(val)
                            } else {
                              // At the last input with existing value - just take the first digit
                              rest.onChange(val.charAt(0))
                            }
                          } else if (val.length === 1) {
                            rest.onChange(val)
                            if (index < digitFields.length - 1) {
                              inputRefs.current[index + 1]?.focus()
                            }
                          } else if (val.length === 0) {
                            rest.onChange("")
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault()
                          const pastedText = e.clipboardData.getData("text")
                          handlePaste(pastedText)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Backspace") {
                            if (rest.value === "") {
                              if (index > 0) {
                                inputRefs.current[index - 1]?.focus()
                              }
                            } else {
                              rest.onChange("")
                            }
                          } else if (e.key === "ArrowLeft" && index > 0) {
                            inputRefs.current[index - 1]?.focus()
                          } else if (
                            e.key === "ArrowRight" &&
                            index < digitFields.length - 1
                          ) {
                            inputRefs.current[index + 1]?.focus()
                          }
                        }}
                      />
                    </TextField>
                  )
                }}
              />
            ))}
          </div>
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button
            buttonType="primary"
            type="submit"
            isPending={verifyMutation.isPending}
          >
            {verifyMutation.isPending
              ? t("auth.verify.submittingButton", { ns: "common" })
              : t("auth.verify.submitButton", { ns: "common" })}
          </Button>
        </fieldset>
      </form>

      {/* Resend Code Link */}
      <Text level={2} className={styles.resendContainer}>
        {t("auth.verify.didntReceiveCode", { ns: "common" })}{" "}
        <Button
          buttonType="primary"
          presentation="link"
          onPress={handleResendCode}
          isDisabled={
            resendMutation.isPending ||
            verifyMutation.isPending ||
            resendCountdown > 0
          }
          style={{
            textDecorationLine:
              resendMutation.isPending ||
              verifyMutation.isPending ||
              resendCountdown > 0
                ? "none"
                : "underline",
            opacity:
              resendMutation.isPending ||
              verifyMutation.isPending ||
              resendCountdown > 0
                ? 0.5
                : 1,
          }}
        >
          {resendMutation.isPending
            ? t("auth.verify.resendingCode", { ns: "common" })
            : showCodeSent
              ? t("auth.verify.codeSent", { ns: "common" })
              : resendCountdown > 0
                ? `Resend in 00:${resendCountdown.toString().padStart(2, "0")}`
                : t("auth.verify.resendCode", { ns: "common" })}
        </Button>
        .
      </Text>
    </>
  )
}
